import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, ArrowLeft, GaugeCircle, PauseCircle, RotateCcw, ShieldAlert } from "lucide-react";
import { Helmet } from "react-helmet-async";

// In-app runbook for the staged helpdesk model rollout.
// Kept as static content (no data fetches) so it's usable during an incident
// even if the edge function or telemetry pipeline is degraded.

export default function HelpdeskRolloutRunbook() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <title>Helpdesk rollout runbook — pause, monitor, rollback</title>
        <meta
          name="description"
          content="Operational runbook for the 10 to 100 percent staged helpdesk model rollout: metrics to watch and how to pause or rollback when errors spike."
        />
      </Helmet>

      <div className="mx-auto max-w-4xl px-4 py-10 space-y-6">
        <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="size-4" /> Back to admin
        </Link>

        <header>
          <h1 className="font-display text-3xl font-bold">Helpdesk rollout runbook</h1>
          <p className="mt-2 text-muted-foreground">
            How to advance, hold, or roll back the staged 10% → 100% helpdesk model rollout.
            Read this before touching <code>helpdesk_rollout.percent</code>.
          </p>
        </header>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GaugeCircle className="size-4 text-primary" /> Rollout stages
            </CardTitle>
            <CardDescription>
              Each stage requires 24 hours of green metrics before promoting to the next.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3 text-sm">
              {[
                ["10%", "Canary. Watch cost + error rate closely — a bad prompt regression here is cheap to unwind."],
                ["25%", "Early adopters. First point where CSAT sample size (~50/day) becomes statistically meaningful."],
                ["50%", "Split. If v1 vs v2 CSAT delta stays within ±5pp for 24h, safe to promote."],
                ["100%", "Full rollout. Leave v1 handler mounted for 7 days for instant rollback."],
              ].map(([pct, note]) => (
                <li key={pct} className="flex gap-3">
                  <Badge variant="secondary" className="font-mono">{pct}</Badge>
                  <span className="text-muted-foreground">{note}</span>
                </li>
              ))}
            </ol>
            <p className="mt-4 text-xs text-muted-foreground">
              Bump the number by updating the single row in <code>helpdesk_rollout</code> (admins only per RLS).
              The next request re-reads it — no deploy required.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldAlert className="size-4 text-primary" /> Metrics to watch (per variant)
            </CardTitle>
            <CardDescription>
              All shown live on the <em>RAG drift &amp; rollout trends</em> panel with a variant filter.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-muted-foreground border-b border-border">
                    <th className="py-2 pr-4 font-medium">Signal</th>
                    <th className="py-2 pr-4 font-medium">Green</th>
                    <th className="py-2 pr-4 font-medium">Warn</th>
                    <th className="py-2 font-medium">Page / rollback</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Nightly eval accuracy", "≥ baseline", "1pp below", "> 3pp below or two consecutive failures"],
                    ["CSAT (👍 rate)", "≥ 85%", "80–85%", "< 75% with ≥ 30 samples"],
                    ["Median latency", "< 4s", "4–6s", "> 8s p50 for 15 minutes"],
                    ["Avg retrieval top-1", "≥ 0.80", "0.72–0.80", "< 0.72 sustained (fallback fires)"],
                    ["Error rate", "< 1%", "1–5%", "> 5% or RLS/denied spike"],
                    ["Cost / resolved", "< $0.02", "$0.02–0.03", "> $0.03 with no CSAT lift"],
                  ].map(([k, g, w, p]) => (
                    <tr key={k} className="border-b border-border/50 last:border-0">
                      <td className="py-2 pr-4">{k}</td>
                      <td className="py-2 pr-4 font-mono text-xs text-primary">{g}</td>
                      <td className="py-2 pr-4 font-mono text-xs text-amber-500">{w}</td>
                      <td className="py-2 font-mono text-xs text-destructive">{p}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PauseCircle className="size-4 text-amber-500" /> Pause (hold current stage)
            </CardTitle>
            <CardDescription>
              Non-destructive. Freezes the split while you investigate.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <ol className="list-decimal pl-5 space-y-1 text-muted-foreground">
              <li>Open the admin support tab and note current <code>percent</code>.</li>
              <li>In the trends panel, filter <code>variant = v2</code> and confirm which signal breached.</li>
              <li>Do not change <code>percent</code>. Post a note in <code>#helpdesk-rollout</code> with the breached signal and window.</li>
              <li>Skip the next scheduled promotion until the signal recovers for 24h.</li>
            </ol>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RotateCcw className="size-4 text-destructive" /> Rollback (revert to v1)
            </CardTitle>
            <CardDescription>
              Instant — takes effect on the next request. No user session interruption.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <ol className="list-decimal pl-5 space-y-1 text-muted-foreground">
              <li>
                Set <code>helpdesk_rollout.percent = 0</code> from the admin panel or SQL editor.
                Every subsequent request buckets to <code>v1</code>.
              </li>
              <li>Capture a screenshot of the trends panel showing the breach for the postmortem.</li>
              <li>
                Open a Sev-2 incident if the trigger was an RLS/denied spike (potential auth regression) or
                &gt; 5% error rate.
              </li>
              <li>
                Keep v1 in place for at least 48h while root cause is identified — do not re-enable the split
                on the same day.
              </li>
            </ol>
            <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs">
              <div className="flex items-center gap-2 font-medium text-destructive">
                <AlertTriangle className="size-3.5" /> RLS-error playbook
              </div>
              <p className="mt-1 text-muted-foreground">
                A sudden rise in <code>helpdesk_tool_audit.outcome = 'denied'</code> almost always means a
                policy or role change. Roll back <em>first</em>, then diff the last migration against the
                <code>user_roles</code> and tool tables. Never bypass RLS by moving tools to service role —
                it defeats the whole tenant isolation model.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Alert routing</CardTitle>
            <CardDescription>Configure per-failure-type Slack destinations.</CardDescription>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            The nightly RAG eval fans out to whichever Slack webhooks are configured in the
            <em> Alert routing </em>panel on the admin support tab. Route accuracy regressions to the
            engineering channel, injection failures to security, and RLS spikes to on-call.
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
